/**
 * Marcações da tela de acompanhamento.
 *
 * "Em atraso" e "Com ocorrência" NÃO são status de frete: o MVP usa o fluxo de status que já
 * existe, e criar status novo é justamente o que o escopo proíbe. Aqui são derivações de exibição
 * sobre o `status.describe` que a base já tem, mais a previsão de entrega e a última posição.
 */

export const MARCACAO = {
  EM_TRANSITO: "EM_TRANSITO",
  COM_ALERTA: "COM_ALERTA",
  EM_ATRASO: "EM_ATRASO",
  ENTREGUE: "ENTREGUE",
  FINALIZADO: "FINALIZADO",
  OUTRO: "OUTRO",
};

export const ROTULOS_DA_MARCACAO = {
  EM_TRANSITO: "Em trânsito",
  COM_ALERTA: "Com ocorrência",
  EM_ATRASO: "Em atraso",
  ENTREGUE: "Entregue",
  FINALIZADO: "Finalizado",
  OUTRO: "Outro status",
};

export const CORES_DA_MARCACAO = {
  EM_TRANSITO: "blue",
  COM_ALERTA: "orange",
  EM_ATRASO: "red",
  ENTREGUE: "green",
  FINALIZADO: "purple",
  OUTRO: "default",
};

/**
 * Acima disso o rastreio parou de chegar. Mesmo limite que a tela antiga já usava.
 *
 * Continua aqui porque é regra de desenho: o mapa marca o intervalo entre duas páginas de trajeto.
 * O alerta de GPS parado não usa mais este número — ele vem do servidor, em `limiares.gpsMin`. Se a
 * Fortio mudar `ALERTA_GPS_MIN` e não mudar isto, a faixa do mapa e a tag da tabela passam a
 * discordar na mesma tela.
 */
export const MINUTOS_SEM_GPS = 45;

export const STATUS_EM_TRANSITO = "Em transito";
export const STATUS_ENTREGUE = "Entregue";
export const STATUS_FINALIZADO = "Finalizado";

function hoje() {
  return new Date().toISOString().slice(0, 10);
}

export function emTransito(frete) {
  return frete?.status?.describe === STATUS_EM_TRANSITO;
}

/**
 * Dias entre a previsão de entrega e hoje. A previsão é `lastDelivery.dateColeta` — o nome engana,
 * mas o valor é a data de operação do último ponto de parada, e é o campo que o Athos indicou na
 * call de 30/07. Conferido em 40 de 40 fretes de homologação.
 */
export function diasDeAtraso(frete) {
  if (!emTransito(frete)) return 0;
  const previsao = frete?.lastDelivery?.dateColeta;
  if (!previsao) return 0;

  const referencia = hoje();
  if (previsao >= referencia) return 0;

  const diferenca = new Date(referencia).getTime() - new Date(previsao).getTime();
  return Math.max(1, Math.round(diferenca / 86400000));
}

/**
 * Frete com pelo menos um dos 11 alertas ativos.
 *
 * A conta saiu daqui. Antes era `Date.now()` contra o `timestamp` gravado pelo aparelho, com o
 * limite fixo neste arquivo — relógio do operador contra relógio do motorista, e um frete que nunca
 * capturou aparecia em alerta junto com um que parou de capturar, que são coisas diferentes. Agora
 * quem decide é o backend, com hora do servidor, e a tela só conta o que veio.
 */
export function comAlerta(frete) {
  return Array.isArray(frete?.alertas) && frete.alertas.length > 0;
}

export function marcacaoDoFrete(frete) {
  const status = frete?.status?.describe;
  if (status === STATUS_FINALIZADO) return MARCACAO.FINALIZADO;
  if (status === STATUS_ENTREGUE) return MARCACAO.ENTREGUE;
  if (!emTransito(frete)) return MARCACAO.OUTRO;

  if (diasDeAtraso(frete) > 0) return MARCACAO.EM_ATRASO;
  if (comAlerta(frete)) return MARCACAO.COM_ALERTA;
  return MARCACAO.EM_TRANSITO;
}
